import React, { forwardRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { Icon } from './Icon';
const ButtonWrapper = styled.button `
  --button-border-radius: ${({ theme }) => theme.style.buttonBorderRadius};
  background-color: transparent;
  color: var(--button-color, ${({ theme }) => theme.primary.foreground});
  display: table;
  border: none;
  position: relative;
  font-weight: ${({ theme }) => theme.style.buttonFontWeight};
  font-size: ${({ theme }) => theme.style.buttonFontSize};
  font-family: inherit;
  margin: 0;
  outline: none;
  user-select: none;
  white-space: nowrap;
  text-align: center;
  appearance: none;
  line-height: ${({ theme }) => theme.style.buttonLineHeight};
  letter-spacing: ${({ theme }) => theme.style.buttonLetterSpacing};
  padding: ${({ theme }) => theme.style.buttonPadding};
  cursor: pointer;
  transform: scale(var(--button-scale, 1)) translateZ(0);
  transition:
    color 0.2s,
    transform 0.2s;
  cursor: ${({ isDisabled }) => isDisabled && 'not-allowed'};
  opacity: ${({ isDisabled, theme }) => isDisabled && theme.style.buttonDisabledOpacity};
  --button-scale: ${({ isPressed, theme }) => isPressed && `var(--button-scale-pressed, ${theme.style.buttonPressedScale})`};

  &:after {
    content: '';
    position: absolute;
    inset: ${({ theme }) => theme.style.buttonPrimaryBackgroundInset};
    border-radius: var(--button-border-radius, 0);
    background-color: var(--button-background, ${({ theme }) => theme.style.buttonPrimaryBackgroundColor});
    box-shadow:
      ${({ theme }) => theme.style.buttonPrimaryBorderProperties} ${({ theme }) => theme.style.buttonPrimaryBorderWidth}
        ${({ theme }) => theme.style.buttonPrimaryBorderColor},
      0 0 0 var(--button-outline, 0px)
        var(--button-outline-color, ${({ theme }) => theme.style.buttonFocussedOutlineColor}),
      ${({ theme }) => theme.style.buttonPrimaryShadow};
    transition:
      background-color 0.2s,
      box-shadow 0.2s,
      color 0.2s,
      transform 0.2s;

    --button-outline: ${({ isFocussed, isPressed, theme }) => isFocussed && !isPressed && theme.style.buttonFocussedOutline};
  }

  svg {
    --icon-color: currentColor;
    --icon-size: ${({ theme }) => theme.style.buttonIconSize};
    flex: 0 0 ${({ theme }) => theme.style.buttonIconSize};
  }

  ${({ fullWidth }) => fullWidth &&
    css `
      width: 100%;
    `}

  ${({ icon }) => icon &&
    css `
      padding: ${({ theme }) => theme.style.buttonPaddingIcon};
    `}

  ${({ icon, iconRight }) => icon &&
    iconRight &&
    css `
      padding: ${({ theme }) => theme.style.buttonPaddingIconRight};
    `}

  ${({ onlyIcon }) => onlyIcon &&
    css `
      padding: ${({ theme }) => theme.style.buttonPaddingIconOnly};

      svg {
        --icon-size: 20px;
      }
    `}

  ${({ variant }) => variant === 'primary' &&
    css `
      ${({ isHovered }) => isHovered &&
        css `
          &:after {
            background-color: var(
              --button-background,
              ${({ theme }) => theme.style.buttonPrimaryHoveredBackgroundColor}
            );
            box-shadow:
              ${({ theme }) => theme.style.buttonPrimaryHoveredBorderProperties}
                ${({ theme }) => theme.style.buttonPrimaryHoveredBorderWidth}
                ${({ theme }) => theme.style.buttonPrimaryHoveredBorderColor},
              0 0 0 var(--button-outline, 0px)
                var(--button-outline-color, ${({ theme }) => theme.style.buttonFocussedOutlineColor}),
              ${({ theme }) => theme.style.buttonPrimaryHoveredShadow};
          }
        `}
    `};

  ${({ variant }) => variant === 'secondary' &&
    css `
      --button-color: ${({ theme }) => theme.style.buttonSecondaryColor};

      &:after {
        --button-background: ${({ theme }) => theme.style.buttonSecondaryBackgroundColor};
        --button-outline-color: ${({ theme }) => theme.style.buttonSecondaryOutlineBorderColor};
        --button-background: ${({ isHovered, theme }) => isHovered && theme.style.buttonSecondaryHoveredBackgroundColor};

        inset: ${({ theme }) => theme.style.buttonSecondaryBackgroundInset};
        background-color: var(--button-background, ${({ theme }) => theme.style.buttonSecondaryBackgroundColor});

        box-shadow:
          ${({ theme }) => theme.style.buttonSecondaryBorderProperties}
            ${({ theme }) => theme.style.buttonSecondaryBorderWidth}
            ${({ theme }) => theme.style.buttonSecondaryBorderColor},
          0 0 0 var(--button-outline, 0px)
            var(--button-outline-color, ${({ theme }) => theme.style.buttonFocussedOutlineColor}),
          ${({ theme }) => theme.style.buttonSecondaryShadow};
      }

      ${({ isHovered }) => isHovered &&
        css `
          &:after {
            --button-background: ${({ theme }) => theme.style.buttonSecondaryHoveredBackgroundColor};
            background-color: var(
              --button-background,
              ${({ theme }) => theme.style.buttonSecondaryHoveredBackgroundColor}
            );

            box-shadow:
              ${({ theme }) => theme.style.buttonSecondaryHoveredBorderProperties}
                ${({ theme }) => theme.style.buttonSecondaryHoveredBorderWidth}
                ${({ theme }) => theme.style.buttonSecondaryHoveredBorderColor},
              0 0 0 var(--button-outline, 0px)
                var(--button-outline-color, ${({ theme }) => theme.style.buttonFocussedOutlineColor}),
              ${({ theme }) => theme.style.buttonSecondaryHoveredShadow};
          }
        `}

      /* ${({ theme }) => theme.id !== 'default' &&
        css `
          --button-background: ${() => theme.secondary.blendToBackground(100)};
          --button-secondary-background: ${() => theme.secondary.blendToBackground(20)};

          --button-secondary-background: ${({ isHovered }) => isHovered && theme.secondary.blendToBackground(30)};
        `} */

      ${({ hasError }) => hasError &&
        css `
          --button-color: ${({ theme }) => theme.danger.background};
          --button-background: ${({ theme }) => theme.danger.blendToBackground(200)};
          --button-secondary-background: ${({ theme }) => theme.danger.blendToBackground(50)};

          --button-secondary-background: ${({ isHovered, theme }) => isHovered && theme.danger.blendToBackground(75)};
        `}
    `}

  ${({ variant }) => variant === 'muted' &&
    css `
      --button-color: ${({ theme }) => theme.secondary.background};

      &:after {
        --button-background: transparent;
        --button-outline-color: ${({ theme }) => theme.secondary.blendToBackground(250)};
        --button-background: ${({ isHovered, theme }) => isHovered && theme.secondary.blendToBackground(25)};
        inset: ${({ theme }) => theme.style.buttonSecondaryBackgroundInset};
        /* box-shadow: 0 0 0 var(--button-outline, 0px)
            var(--button-outline-color, ${({ theme }) => theme.primary.blendToBackground(100)}),
          0px 0px 0px 1px ${({ theme }) => theme.secondary.blendToBackground(200)}; */

        box-shadow:
          ${({ theme }) => theme.style.buttonSecondaryBorderProperties}
            ${({ theme }) => theme.style.buttonSecondaryBorderWidth}
            ${({ theme }) => theme.style.buttonSecondaryBorderColor},
          0 0 0 var(--button-outline, 0px)
            var(--button-outline-color, ${({ theme }) => theme.style.buttonFocussedOutlineColor});

        /* ${({ theme }) => theme.id !== 'default' &&
        css `
            --button-background: ${() => theme.secondary.blendToBackground(25)};
            --button-background: ${({ isHovered }) => isHovered && theme.secondary.blendToBackground(50)};
            --button-shine: ${() => theme.secondary.blendToBackground(75)};
            --button-shine-y: 0px;
          `} */

        ${({ hasError }) => hasError &&
        css `
            --button-color: ${({ theme }) => theme.danger.background};
            --button-background: ${({ theme }) => theme.danger.blendToBackground(75)};
            --button-outline-color: ${({ theme }) => theme.danger.blendToBackground(250)};
          `}
      }
    `}

  ${({ variant }) => variant === 'foreground' &&
    css `
      --button-background: ${({ theme }) => theme.foreground};
      --button-color: ${({ theme }) => theme.background};
      --button-outline-color: ${({ theme }) => theme.secondary.blendToForeground(700)};
      --button-background: ${({ isHovered, theme }) => isHovered && theme.secondary.blendToForeground(150)};
      --button-color: ${({ theme, isDisabled }) => isDisabled && theme.secondary.blendToForeground(700)};

      &:after {
        box-shadow:
          0 0 0 var(--button-outline, 0px)
            var(--button-outline-color, ${({ theme }) => theme.primary.blendToBackground(1000, 300)}),
          inset 0 -1px 0 ${({ theme }) => theme.secondary.blendToBackground(2000)},
          inset 0 0 0 1px ${({ theme }) => theme.secondary.blendToBackground(1200)},
          ${({ theme }) => theme.style.raisedShadow};
      }
    `}

  ${({ variant }) => variant === 'danger' &&
    css `
      --button-background: ${({ theme }) => theme.danger.background};
      --button-color: ${({ theme }) => theme.danger.foreground};
      --button-outline-color: ${({ theme }) => theme.danger.blendToBackground(1000, 300)};

      --button-background: ${({ isHovered, theme }) => isHovered && theme.danger.blendToBackground(1100)};

      &:after {
        box-shadow:
          0 0 0 var(--button-outline, 0px)
            var(--button-outline-color, ${({ theme }) => theme.primary.blendToBackground(1000, 300)}),
          inset 0 -1px 0 ${({ theme }) => theme.danger.blendToBackground(2000)},
          inset 0 0 0 1px ${({ theme }) => theme.danger.blendToBackground(1200)},
          ${({ theme }) => theme.style.raisedShadow};
      }
    `}

  ${({ variant }) => (variant === 'subtle' || variant === 'subtle-primary') &&
    css `
      --button-background: transparent;
      --button-color: ${({ theme }) => theme.secondary.blendToBackground(800)};

      --button-color: ${({ isHovered, theme }) => isHovered && theme.secondary.background};
      /* padding: 0; */
      font-weight: ${({ theme }) => theme.style.buttonSubtleFontWeight};

      --button-outline-color: ${({ isFocussed, isPressed, theme }) => isFocussed && !isPressed && theme.primary.blendToBackground(100)};
      --button-outline-color: transparent;

      &:after {
        box-shadow:
          inset 0 0 0 24px var(--button-outline-color, ${({ theme }) => theme.primary.blendToBackground(100)}),
          0 0 0 8px var(--button-outline-color, ${({ theme }) => theme.primary.blendToBackground(100)});
      }

      ${() => variant === 'subtle-primary' &&
        css `
          --button-color: ${({ theme }) => theme.primary.background};

          --button-color: ${({ isHovered, theme }) => isHovered && theme.primary.blendToBackground(1200)};

          --button-outline-color: ${({ isFocussed, isPressed, theme }) => isFocussed && !isPressed && theme.style.buttonSecondaryOutlineBorderColor};
          --button-outline-color: transparent;
        `}
    `}

  ${({ size }) => size === 'small' &&
    css `
      --button-border-radius: ${({ theme }) => theme.style.buttonSmallBorderRadius};
      font-weight: ${({ theme }) => theme.style.buttonSmallFontWeight};
      font-size: ${({ theme }) => theme.style.buttonSmallFontSize};
      line-height: ${({ theme }) => theme.style.buttonSmallLineHeight};
      padding: ${({ theme }) => theme.style.buttonSmallPadding};

      svg {
        --icon-size: ${({ theme }) => theme.style.buttonSmallIconSize};
        flex: 0 0 ${({ theme }) => theme.style.buttonSmallIconSize};
      }

      ${({ icon }) => icon &&
        css `
          padding: ${({ theme }) => theme.style.buttonSmallPaddingIcon};
        `}

      ${({ icon, iconRight }) => icon &&
        iconRight &&
        css `
          padding: ${({ theme }) => theme.style.buttonSmallPaddingIconRight};
        `}

      ${({ onlyIcon }) => onlyIcon &&
        css `
          padding: ${({ theme }) => theme.style.buttonSmallPaddingIconOnly};

          svg {
            --icon-size: 16px;
          }
        `}
    `}

  ${({ size }) => size === 'tiny' &&
    css `
      --button-border-radius: ${({ theme }) => theme.style.buttonTinyBorderRadius};
      font-weight: ${({ theme }) => theme.style.buttonTinyFontWeight};
      font-size: ${({ theme }) => theme.style.buttonTinyFontSize};
      line-height: ${({ theme }) => theme.style.buttonTinyLineHeight};
      padding: ${({ theme }) => theme.style.buttonTinyPadding};

      svg {
        --icon-size: ${({ theme }) => theme.style.buttonTinyIconSize};
        flex: 0 0 ${({ theme }) => theme.style.buttonTinyIconSize};
      }

      ${({ icon }) => icon &&
        css `
          padding: ${({ theme }) => theme.style.buttonTinyPaddingIcon};
        `}

      ${({ icon, iconRight }) => icon &&
        iconRight &&
        css `
          padding: ${({ theme }) => theme.style.buttonTinyPaddingIconRight};
        `}

      ${({ onlyIcon }) => onlyIcon &&
        css `
          padding: ${({ theme }) => theme.style.buttonTinyPaddingIconOnly};
        `}
    `}
`;
export const ButtonInner = styled.div `
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: var(--button-align, center);
  align-items: center;
  gap: ${({ theme }) => theme.style.buttonGap};
  min-height: ${({ theme }) => theme.style.buttonLineHeight};

  transition:
    transform 0.2s,
    opacity 0.2s;

  ${({ isLoading }) => isLoading &&
    css `
      opacity: 0;
      transform: translateY(12px) scale(0.75);
    `}

  ${({ size }) => size === 'small' &&
    css `
      gap: ${({ theme }) => theme.style.buttonSmallGap};
      min-height: ${({ theme }) => theme.style.buttonSmallLineHeight};
    `}

  ${({ size }) => size === 'tiny' &&
    css `
      gap: ${({ theme }) => theme.style.buttonTinyGap};
      min-height: ${({ theme }) => theme.style.buttonTinyLineHeight};
    `}
`;
const Loading = styled(Icon.Loading) `
  --icon-color: currentColor;

  position: absolute;
  z-index: 1;
  left: 50%;
  top: 50%;
  transform: translateX(-50%) translateY(-50%);
  transition:
    transform 0.2s,
    opacity 0.2s;

  ${({ isLoading }) => !isLoading &&
    css `
      opacity: 0;
      transform: translateY(-12px) scale(0.75);
    `}
`;
export const Button = forwardRef((props, ref) => {
    const [isHoveredState, setIsHoveredState] = useState(false);
    const [isFocussedState, setIsFocussedState] = useState(false);
    const [isPressedState, setIsPressedState] = useState(false);
    const styleProps = {
        isHovered: props.isHovered ?? isHoveredState,
        isFocussed: props.isFocussed ?? isFocussedState,
        isPressed: props.isPressed ?? isPressedState,
        isDisabled: props.isDisabled ?? false,
        variant: props.variant ?? 'primary',
        size: props.size ?? 'default',
        icon: props.icon ?? false,
        hasError: props.hasError ?? false,
        isLoading: props.isLoading ?? false,
        iconRight: props.iconRight ?? false,
        onlyIcon: !props.children,
        fullWidth: props.fullWidth ?? false,
    };
    const handleHoverChange = (value) => {
        if (props.onHoverChange && value !== isHoveredState) {
            props.onHoverChange(value);
        }
        if (!value) {
            handlePressChange(false);
        }
        setIsHoveredState(value);
    };
    const handleFocusChange = (value) => {
        if (props.onFocusChange && value !== isFocussedState) {
            props.onFocusChange(value);
        }
        setIsFocussedState(value);
    };
    const handlePressChange = (value) => {
        if (props.onPressChange && value !== isPressedState) {
            props.onPressChange(value);
        }
        if (value) {
            handleFocusChange(true);
        }
        setIsPressedState(value);
    };
    const handleClick = (event) => {
        if (props.isLoading) {
            return;
        }
        if (props.onClick) {
            props.onClick(event);
        }
        handleFocusChange(false);
        handlePressChange(false);
    };
    return (React.createElement(ButtonWrapper, { onClick: handleClick, onPointerEnter: () => handleHoverChange(true), onPointerMove: () => handleHoverChange(true), onPointerLeave: () => handleHoverChange(false), onFocus: () => handleFocusChange(true), onBlur: () => handleFocusChange(false), onPointerDown: () => handlePressChange(true), onPointerUp: () => handlePressChange(false), disabled: styleProps.isDisabled, className: props.className, type: props.type ?? 'submit', ref: ref, ...styleProps },
        React.createElement(ButtonInner, { ...styleProps },
            !props.iconRight && props.icon,
            props.children,
            props.iconRight && props.icon),
        props.content,
        React.createElement(Loading, { ...styleProps })));
});
//# sourceMappingURL=Button.js.map